/**
 * Shared AI Risk Briefing UI used by both v1 (TradeSignals) and v2
 * (TradeSignalsV2) pages. Two parts:
 *   <AIBriefTag>     — compact chip ("AI · Risk High" + ?/×) for the row summary
 *   <AIBriefPanel>   — full 3-sentence brief panel that drops below the row
 *                       when the tag is clicked.
 */
import React from 'react';

export interface BriefForCard {
  signal_id: string;
  briefing: string;
  riskLevel: string;
  watchFor: string;
  generatedAt: string;
  modelVersion: string;
}

const PALETTES: Record<string, { fg: string; bg: string }> = {
  low:    { fg: '#16a34a', bg: '#16a34a20' },
  medium: { fg: '#d97706', bg: '#d9770620' },
  high:   { fg: '#dc2626', bg: '#dc262620' },
};
const DEFAULT_PALETTE = { fg: '#9ca3af', bg: '#1a2332' };

export const AIBriefTag: React.FC<{
  brief: BriefForCard;
  open: boolean;
  onToggle: () => void;
}> = ({ brief, open, onToggle }) => {
  const palette = PALETTES[brief.riskLevel] || DEFAULT_PALETTE;
  return (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); onToggle(); }}
      title="Show AI risk briefing"
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '0.3rem',
        padding: '0.15rem 0.5rem',
        borderRadius: 10,
        background: palette.bg,
        color: palette.fg,
        fontSize: '0.66rem',
        fontWeight: 700,
        textTransform: 'uppercase',
        letterSpacing: '0.03em',
        cursor: 'pointer',
        userSelect: 'none',
        border: `1px solid ${palette.fg}40`,
      }}
    >
      AI · Risk {brief.riskLevel}
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 13,
          height: 13,
          borderRadius: '50%',
          border: `1px solid ${palette.fg}`,
          fontSize: '0.55rem',
          fontWeight: 700,
          lineHeight: 1,
        }}
      >
        {open ? '×' : '?'}
      </span>
    </button>
  );
};

export const AIBriefPanel: React.FC<{ brief: BriefForCard }> = ({ brief }) => {
  const palette = PALETTES[brief.riskLevel] || DEFAULT_PALETTE;
  return (
    <div style={{
      margin: '0 0 0.75rem',
      padding: '0.65rem 0.85rem',
      background: '#0f172a',
      border: '1px solid #374151',
      borderLeft: `3px solid ${palette.fg}`,
      borderRadius: 6,
      fontSize: '0.84rem',
    }}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 5 }}>
        <strong style={{ fontSize: '0.85rem', color: '#f9fafb' }}>AI Risk Briefing</strong>
        <span style={{
          color: palette.fg,
          fontSize: '0.62rem',
          fontWeight: 700,
          letterSpacing: 0.4,
          textTransform: 'uppercase',
        }}>{brief.riskLevel} risk</span>
      </div>
      <div style={{ color: '#f9fafb', lineHeight: 1.5, marginBottom: 6, whiteSpace: 'pre-wrap' }}>
        {brief.briefing}
      </div>
      {brief.watchFor && (
        <div style={{ color: '#9ca3af', fontSize: '0.76rem', fontStyle: 'italic', marginBottom: 4 }}>
          Watch: <span style={{ color: '#f9fafb', fontStyle: 'normal' }}>{brief.watchFor}</span>
        </div>
      )}
      {brief.generatedAt && (
        <div style={{ color: '#6b7280', fontSize: '0.7rem' }}>
          Generated {new Date(brief.generatedAt).toLocaleString('en-US', { timeZone: 'America/New_York', hour: 'numeric', minute: '2-digit', month: 'short', day: 'numeric' })} ET · {brief.modelVersion}
        </div>
      )}
    </div>
  );
};
